import { useCallback, useEffect, useRef, useState } from "react";

function getRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * Browser speech-to-text via Web Speech API (Chrome, Edge, Safari).
 */
export function useSpeechRecognition({ lang = "en-US", onFinal } = {}) {
  const ctor = getRecognitionCtor();
  const recRef = useRef(null);
  const [supported] = useState(!!ctor);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ctor) return undefined;
    const rec = new ctor();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = lang;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimText = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const t = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) finalText += t;
        else interimText += t;
      }
      if (interimText) setInterim(interimText.trim());
      if (finalText) {
        const trimmed = finalText.trim();
        setInterim("");
        onFinal?.(trimmed);
      }
    };

    rec.onerror = (e) => {
      const code = e.error || "unknown";
      if (code !== "aborted" && code !== "no-speech") {
        setError(code === "not-allowed" ? "Microphone permission denied" : `Voice error: ${code}`);
      }
      setListening(false);
      setInterim("");
    };

    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    recRef.current = rec;
    return () => {
      try { rec.abort(); } catch (err) { /* noop */ }
      recRef.current = null;
    };
  }, [ctor, lang, onFinal]);

  const start = useCallback(() => {
    if (!recRef.current || listening) return false;
    setError(null);
    setInterim("");
    try {
      recRef.current.start();
      setListening(true);
      return true;
    } catch (e) {
      setError("Could not start microphone");
      return false;
    }
  }, [listening]);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch (e) { /* noop */ }
    setListening(false);
    setInterim("");
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  return { supported, listening, interim, error, start, stop, toggle, clearError: () => setError(null) };
}
